import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BRAND, FORM_ACTION } from '@/mocks/brandData';

export interface FinalCTAProps {
  headline: string;
  subtext: string;
  backgroundImage: string;
}

export default function FinalCTA({ headline, subtext, backgroundImage }: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { ref, visible } = useScrollAnimation(0.05);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => params.append(k, v as string));
    try {
      await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setError(`Something went wrong. Please call ${BRAND.phone} directly.`);
    }
  };

  return (
    <section id="quote" className="relative py-24 md:py-36 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={backgroundImage} alt="JC Cooks catering experience" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(26,26,24,0.97) 0%, rgba(26,26,24,0.90) 50%, rgba(26,26,24,0.85) 100%)' }}></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,169,97,0.08)_0%,transparent_60%)]"></div>
      </div>

      {/* Gold top border */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-jc-gold/60 to-transparent z-10"></div>

      <div
        ref={ref}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">

          {/* Left: brand block */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-jc-gold"></span>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">Reserve Your Experience</span>
            </div>
            <h2 className="font-serif text-[clamp(2rem,4.5vw,3.6rem)] font-light text-white leading-[1.1] mb-7">{headline}</h2>
            <p className="text-white/50 text-[0.95rem] leading-relaxed mb-12 max-w-md">{subtext}</p>

            {/* Phone CTA */}
            <a href={BRAND.phoneHref} className="flex items-center gap-5 group w-fit mb-10">
              <div className="w-16 h-16 flex items-center justify-center bg-jc-gold rounded-full group-hover:bg-white transition-all duration-300 flex-shrink-0 shadow-[0_0_30px_rgba(201,169,97,0.3)]">
                <i className="ri-phone-line text-jc-charcoal text-2xl"></i>
              </div>
              <div>
                <p className="text-white/35 text-[10px] font-semibold tracking-[0.2em] uppercase mb-0.5">Call to Book</p>
                <p className="font-serif text-[2rem] text-white leading-none">{BRAND.phone}</p>
              </div>
            </a>

            {/* Contact info */}
            <div className="space-y-3 pt-3 border-t border-white/8">
              <div className="flex items-center gap-3 text-white/35 text-sm">
                <i className="ri-map-pin-line text-jc-gold/60 text-base flex-shrink-0"></i>
                <span>{BRAND.fullAddress}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <i className="ri-mail-line text-jc-gold/60 text-base flex-shrink-0"></i>
                <a href={`mailto:${BRAND.email}`} className="text-white/35 hover:text-jc-gold transition-colors">{BRAND.email}</a>
              </div>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
              {['20+ Years Experience', 'Scratch-Cooked Meals', 'Award-Winning Service'].map((t) => (
                <div key={t} className="flex items-center gap-1.5 text-white/25 text-xs">
                  <i className="ri-check-line text-jc-gold/50 text-sm"></i>
                  {t}
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-jc-gold/15 border border-jc-gold/30 rounded-full mx-auto mb-6">
                  <i className="ri-check-double-line text-3xl text-jc-gold"></i>
                </div>
                <h3 className="font-serif text-2xl text-white mb-3">Request Received</h3>
                <p className="text-white/45 text-sm leading-relaxed max-w-[260px] mx-auto">
                  Chef Joyce will be in touch with you shortly to discuss your event.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-[1.6rem] text-white leading-tight mb-1.5">Request a Custom Quote</h3>
                <p className="text-white/35 text-xs mb-7 leading-relaxed">Select your service and share your vision — we&apos;ll craft the perfect menu.</p>
                {error && <p className="text-red-300 text-sm mb-4 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-3.5">
                  <div className="grid grid-cols-2 gap-3.5">
                    <input type="text" name="name" required placeholder="Full Name"
                      className="col-span-2 sm:col-span-1 w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder-white/25 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors" />
                    <input type="email" name="email" required placeholder="Email Address"
                      className="col-span-2 sm:col-span-1 w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder-white/25 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors" />
                  </div>
                  <input type="tel" name="phone" placeholder="Phone Number"
                    className="w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder-white/25 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors" />
                  <select name="service"
                    className="w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white/60 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors cursor-pointer" style={{ backgroundColor: 'rgba(26,26,24,0.8)' }}>
                    <option value="">Select Service Type</option>
                    <option value="private-chef">Private Chef Service</option>
                    <option value="event-catering">Event Catering</option>
                    <option value="corporate">Corporate Catering</option>
                    <option value="weekly-meals">Weekly Meal Service</option>
                    <option value="wedding">Wedding Catering</option>
                    <option value="holiday">Holiday Catering</option>
                  </select>
                  <div className="grid grid-cols-2 gap-3.5">
                    <input type="text" name="event_date" placeholder="Event Date"
                      className="w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder-white/25 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors" />
                    <input type="text" name="guest_count" placeholder="Guest Count"
                      className="w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder-white/25 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors" />
                  </div>
                  <textarea name="message" placeholder="Share your vision, dietary needs, or any special requests..." rows={3} maxLength={500}
                    className="w-full bg-white/8 border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder-white/25 text-sm focus:outline-none focus:border-jc-gold/60 transition-colors resize-none"></textarea>
                  <button type="submit"
                    className="w-full py-4 bg-jc-gold text-jc-charcoal font-bold text-[10px] tracking-[0.2em] uppercase rounded-full hover:bg-white transition-all duration-300 cursor-pointer shadow-[0_8px_24px_rgba(201,169,97,0.25)] whitespace-nowrap">
                    Send My Quote Request
                  </button>
                  <p className="text-white/20 text-[11px] text-center">Or call us directly at <a href={BRAND.phoneHref} className="text-jc-gold/60 hover:text-jc-gold transition-colors">{BRAND.phone}</a></p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
